// Single source of truth for the gamified pricing tiers.
//
// Six tiers named after the in-app points tiers (Script Kiddie → Zero-Day).
// Free = first quest only, then a hard paywall. Paid plans start at
// Operator ($20) and step up to Zero-Day ($1000) — each tier ships a larger
// monthly credit budget plus more unlocks (premium models, paths, leaderboard
// boosts, etc.). When a user exceeds 100% of their tier's budget the Hub blocks
// further AI calls and surfaces the next-tier upsell modal.
//
// Credit cost per action (enforced in edge functions):
//   - chat (fast model)    → 1 credit
//   - chat (premium model) → 3 credits   (GPT-5, Gemini 2.5 Pro)
//   - image generation     → 5 credits
//   - research run         → 8 credits
//
// The 80% threshold triggers a soft warning; 100% is a hard block.
//
// IMPORTANT: keep `id` values in sync with the `plan` string returned by the
// `check-subscription` edge function and accepted by `create-checkout`.

export type PlanId =
  | "script_kiddie"
  | "operator"
  | "hacker"
  | "wizard"
  | "architect"
  | "zero_day";

export type Quota = {
  /** Monthly credit budget. `null` = unlimited (fair use). */
  monthlyCredits: number | null;
  /** Whether premium models (GPT-5, Gemini 2.5 Pro) are unlocked. */
  premiumModels: boolean;
  /** Whether GPT-5 with high reasoning effort is unlocked. */
  gpt5HighEffort: boolean;
  /** Points multiplier on every quest completed at this tier. */
  xpMultiplier: number;
  /** Number of learning paths unlocked. `null` = all. */
  pathsUnlocked: number | null;
};

export type Plan = {
  id: PlanId;
  /** Tier display name shown to users (e.g. "Operator"). */
  name: string;
  /** Short pitch shown under the name on the pricing card. */
  tagline: string;
  /** USD per month. 0 for the free tier. */
  priceMonthly: number;
  /** Human-readable price label. */
  priceLabel: string;
  /** USD billed yearly (full charge once per year). 0 for the free tier.
   *  Computed from `priceMonthly` × 12 × (1 − YEARLY_DISCOUNT). */
  priceYearly: number;
  /** Effective per-month price when billed yearly (rounded to nearest dollar). */
  priceYearlyPerMonth: number;
  /** Human-readable yearly total label, e.g. "$200" or "Free". */
  priceYearlyLabel: string;
  /** Human-readable per-month-equivalent label when billed yearly, e.g. "$17". */
  priceYearlyPerMonthLabel: string;
  /** Bullet list shown on the pricing card. */
  features: string[];
  /** CTA button copy. */
  cta: string;
  /** Visually highlight the card. */
  highlighted?: boolean;
  quota: Quota;
};

/** 80% warning threshold — UI shows "you're nearly out" at this point. */
export const WARN_THRESHOLD = 0.8;

/** Yearly billing discount vs paying monthly (≈ 2 months free). */
export const YEARLY_DISCOUNT = 1 / 6; // ~16.67% off

/** Selectable billing cadence on the Pricing page. */
export type BillingCycle = "monthly" | "yearly";

/** Compute yearly + per-month-equivalent prices for a given monthly price. */
function deriveYearlyPricing(priceMonthly: number) {
  if (priceMonthly <= 0) {
    return {
      priceYearly: 0,
      priceYearlyPerMonth: 0,
      priceYearlyLabel: "$0",
      priceYearlyPerMonthLabel: "$0",
    };
  }
  const rawYearly = priceMonthly * 12 * (1 - YEARLY_DISCOUNT);
  const priceYearly = Math.round(rawYearly);
  const priceYearlyPerMonth = Math.round(priceYearly / 12);
  return {
    priceYearly,
    priceYearlyPerMonth,
    priceYearlyLabel: `$${priceYearly.toLocaleString("en-US")}`,
    priceYearlyPerMonthLabel: `$${priceYearlyPerMonth.toLocaleString("en-US")}`,
  };
}

/** Credit cost per Hub action — kept in sync with edge function enforcement. */
export const CREDIT_COSTS = {
  chatFast: 1,
  chatPremium: 3,
  image: 5,
  research: 8,
} as const;

/** Plan definitions without derived yearly fields — source of truth. */
type PlanSeed = Omit<
  Plan,
  "priceYearly" | "priceYearlyPerMonth" | "priceYearlyLabel" | "priceYearlyPerMonthLabel"
>;

const PLAN_SEEDS: Record<PlanId, PlanSeed> = {
  script_kiddie: {
    id: "script_kiddie",
    name: "Script Kiddie",
    tagline: "Run your first hack. No card. No catch.",
    priceMonthly: 0,
    priceLabel: "$0",
    cta: "Start free",
    quota: {
      monthlyCredits: 20, // ~1 quest worth of Hub usage
      premiumModels: false,
      gpt5HighEffort: false,
      xpMultiplier: 1,
      pathsUnlocked: 1,
    },
    features: [
      "Complete your first quest free",
      "Earn your first points & tier badge",
      "Browse the full tool directory",
      "Save guides & research",
      "Locked: Hub usage after first quest",
    ],
  },
  operator: {
    id: "operator",
    name: "Operator",
    tagline: "Where serious AI users start. One sub for every model.",
    priceMonthly: 20,
    priceLabel: "$20",
    cta: "Become Operator",
    highlighted: true,
    quota: {
      monthlyCredits: 200,
      premiumModels: true,
      gpt5HighEffort: false,
      xpMultiplier: 1,
      pathsUnlocked: 3,
    },
    features: [
      "200 credits / mo (~200 chats or 40 images)",
      "GPT-5, Gemini 2.5 Pro, image gen, research",
      "3 learning paths unlocked",
      "Compete on the global leaderboard",
      "Auto-prompt to upgrade when you outgrow it",
    ],
  },
  hacker: {
    id: "hacker",
    name: "Hacker",
    tagline: "For people who run AI every day.",
    priceMonthly: 50,
    priceLabel: "$50",
    cta: "Level up to Hacker",
    quota: {
      monthlyCredits: 600,
      premiumModels: true,
      gpt5HighEffort: false,
      xpMultiplier: 1.25,
      pathsUnlocked: 6,
    },
    features: [
      "600 credits / mo (3× Operator)",
      "1.25× points on every quest",
      "6 learning paths unlocked",
      "Priority response times",
      "All Operator features",
    ],
  },
  wizard: {
    id: "wizard",
    name: "Wizard",
    tagline: "Power users. Builders. People who ship daily.",
    priceMonthly: 200,
    priceLabel: "$200",
    cta: "Ascend to Wizard",
    quota: {
      monthlyCredits: 3_000,
      premiumModels: true,
      gpt5HighEffort: true,
      xpMultiplier: 1.5,
      pathsUnlocked: null,
    },
    features: [
      "3,000 credits / mo",
      "1.5× points multiplier",
      "GPT-5 with high reasoning effort",
      "All learning paths unlocked",
      "Early access to new Hub panels",
    ],
  },
  architect: {
    id: "architect",
    name: "Architect",
    tagline: "Teams + heavy users. AI as a daily operating system.",
    priceMonthly: 500,
    priceLabel: "$500",
    cta: "Become Architect",
    quota: {
      monthlyCredits: 10_000,
      premiumModels: true,
      gpt5HighEffort: true,
      xpMultiplier: 2,
      pathsUnlocked: null,
    },
    features: [
      "10,000 credits / mo",
      "2× points multiplier",
      "Direct support channel",
      "Early access to upcoming Code Review, Doc Q&A, Voice panels",
      "All Wizard features",
    ],
  },
  zero_day: {
    id: "zero_day",
    name: "Zero-Day",
    tagline: "Unlimited. The top of the leaderboard lives here.",
    priceMonthly: 1000,
    priceLabel: "$1,000",
    cta: "Go Zero-Day",
    quota: {
      monthlyCredits: null,
      premiumModels: true,
      gpt5HighEffort: true,
      xpMultiplier: 3,
      pathsUnlocked: null,
    },
    features: [
      "Unlimited credits (fair-use)",
      "3× points multiplier — fastest path to Architect tier",
      "Permanent Zero-Day badge on leaderboard",
      "White-glove onboarding & dedicated Slack",
      "Influence the product roadmap",
    ],
  },
};

/** Public plan map with monthly + derived yearly pricing baked in. */
export const PLANS: Record<PlanId, Plan> = Object.fromEntries(
  (Object.keys(PLAN_SEEDS) as PlanId[]).map((id) => [
    id,
    { ...PLAN_SEEDS[id], ...deriveYearlyPricing(PLAN_SEEDS[id].priceMonthly) },
  ]),
) as Record<PlanId, Plan>;

export const PLAN_ORDER: PlanId[] = [
  "script_kiddie",
  "operator",
  "hacker",
  "wizard",
  "architect",
  "zero_day",
];

/** Paid tiers only, in upgrade order. */
export const PAID_PLAN_ORDER: PlanId[] = [
  "operator",
  "hacker",
  "wizard",
  "architect",
  "zero_day",
];

/** True if `plan` includes the entitlement of `required`. */
export function planAtLeast(plan: PlanId, required: PlanId): boolean {
  return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(required);
}

/** Returns the next paid tier above `plan`, or `null` if already at the top. */
export function nextTier(plan: PlanId): PlanId | null {
  const idx = PAID_PLAN_ORDER.indexOf(plan);
  if (idx === -1) return "operator"; // free → first paid tier
  if (idx === PAID_PLAN_ORDER.length - 1) return null;
  return PAID_PLAN_ORDER[idx + 1];
}

/** Comparison rows for the "what you'd pay elsewhere" receipts table. */
export const COMPETITOR_COMPARISON: Array<{
  capability: string;
  elsewhere: string;
  elsewherePrice: number;
}> = [
  { capability: "GPT-5 chat", elsewhere: "ChatGPT Plus", elsewherePrice: 40 },
  { capability: "Gemini 2.5 Pro", elsewhere: "Gemini Advanced", elsewherePrice: 40 },
  { capability: "Image generation", elsewhere: "Midjourney Standard", elsewherePrice: 30 },
  { capability: "Live web research", elsewhere: "Perplexity Pro", elsewherePrice: 20 },
  { capability: "Premium reasoning models", elsewhere: "Claude Pro", elsewherePrice: 40 },
  { capability: "Prompt library + builder", elsewhere: "PromptBase Pro", elsewherePrice: 30 },
];

export const COMPETITOR_TOTAL = COMPETITOR_COMPARISON.reduce(
  (sum, row) => sum + row.elsewherePrice,
  0,
);
